import { useEffect, useState, useRef } from 'react';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { fillColor, fillPercent } from '../utils/fillColor';
import { statusAge } from '../utils/statusAge';
import EventDetailSheet from '../components/EventDetailSheet';

// ── Payroll step 2 (mobile): per-shift earnings, mirrors the backend calc ─────
function mEarned(payAmount, payUnit, timeStart, timeEnd) {
  const amt = (payAmount === '' || payAmount === null || payAmount === undefined || isNaN(Number(payAmount))) ? null : Number(payAmount);
  if (amt === null) return { hours: null, earned: null };
  if (payUnit === 'daily') return { hours: null, earned: amt };
  if (payUnit === 'per_event') return { hours: null, earned: amt }; // Session P: flat for the whole booking
  if (!timeStart || !timeEnd) return { hours: null, earned: null };
  const [sh, sm] = String(timeStart).split(':').map(Number);
  const [eh, em] = String(timeEnd).split(':').map(Number);
  if ([sh, sm, eh, em].some(n => isNaN(n))) return { hours: null, earned: null };
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60; // overnight
  const hours = mins / 60;
  return { hours, earned: amt * hours };
}
const mRateStr = (amt, unit) => {
  if (amt === '' || amt === null || amt === undefined || isNaN(Number(amt))) return '';
  if (unit === 'per_event') return `R${Number(amt)} for the event`; // Session P
  return unit === 'daily' ? `R${Number(amt)}/day` : `R${Number(amt)}/hr`;
};
const fmtM = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : v.toFixed(2); };
const fmtH = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); };

// ── Bottom nav for mobile agent view ─────────────────────────────────────────
function AgentBottomNav({ active, setActive }) {
  const tabs = [
    { key: 'applications', label: 'Applications', icon: (a) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2.2 : 1.8} strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2v6h6M9 13h6M9 17h4"/>
      </svg>
    )},
    { key: 'events', label: 'Events', icon: (a) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2.2 : 1.8} strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
        {a && <path d="M8 14h2M12 14h2M8 18h2"/>}
      </svg>
    )},
    { key: 'staff', label: 'Staff', icon: (a) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2.2 : 1.8} strokeLinecap="round">
        <circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87"/>
      </svg>
    )},
    { key: 'comms', label: 'Comms', icon: (a) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2.2 : 1.8} strokeLinecap="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    )},
  ];

  return (
    <nav className="bottom-nav">
      {tabs.map(tab => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            className={`nav-item ${isActive ? 'active' : ''}`}
            onClick={() => setActive(tab.key)}
          >
            {tab.icon(isActive)}
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

// ── Applications tab ──────────────────────────────────────────────────────────
// ── Applicant info flash-view (photo + demographics — mirrors desktop) ────────
// Height, weight, race only. Never bank details (not in the payload at all).
function MStaffAvatar({ picture, name, size = 36 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, color: '#fff', overflow: 'hidden', flexShrink: 0 }}>
      {picture ? <img src={picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (name || 'S').charAt(0).toUpperCase()}
    </div>
  );
}

function MInfoButton({ onClick }) {
  return (
    <button onClick={onClick} title="View staff info"
      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ width: 12, height: 12 }} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
      </svg>
    </button>
  );
}

function MApplicantInfoFlash({ app, onClose }) {
  const fmt = (v, unit) => (v !== null && v !== undefined && v !== '') ? `${Number(v)} ${unit}` : '—';
  // Session AA — KEEP IN SYNC with ApplicantInfoFlash in AgentDashboard.js.
  // Height/Weight/Race are consent-gated and blanked without it; Gender,
  // T-shirt and Pants size are ordinary personal information and always shown.
  const consented = Number(app.profile_consent) === 1;
  const rows = [
    ['Height', consented ? fmt(app.height_cm, 'cm') : '—'],
    ['Weight', consented ? fmt(app.weight_kg, 'kg') : '—'],
    ['Race', consented ? (app.race || '—') : '—'],
    ['Gender', app.gender || '—'],
    ['T-shirt', app.tshirt_size || '—'],
    ['Pants size', app.pants_size != null ? String(app.pants_size) : '—'],
  ];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 24px', width: '100%', maxWidth: 300, boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 14 }}>
          <MStaffAvatar picture={app.picture} name={app.name} size={72} />
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginTop: 10, textAlign: 'center' }}>{app.name}</div>
        </div>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</span>
          </div>
        ))}
        {!consented && (
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted, var(--text-secondary))', lineHeight: 1.5 }}>
            Height, weight and race are hidden — this staff member has not consented to sharing them.
          </div>
        )}
        <button onClick={onClose} className="btn btn-ghost btn-sm" style={{ width: '100%', marginTop: 14 }}>Close</button>
      </div>
    </div>
  );
}

function MobileApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('all');
  const [conflictMsg, setConflictMsg] = useState('');
  const [infoApp, setInfoApp] = useState(null);

  useEffect(() => {
    axios.get('/api/agent/applications').then(r => setApps(r.data)).finally(() => setLoading(false));
  }, []);

  // Manual refresh (17 Jul) — silent refetch, mirrors desktop Applications
  const [refreshing, setRefreshing] = useState(false);
  const manualLoad = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await axios.get('/api/agent/applications');
      setApps(r.data);
    } finally { setRefreshing(false); }
  };

  const recalcApproved = (apps, changedId, newStatus) => {
    const updated = apps.map(x => x.id === changedId ? { ...x, status: newStatus } : x);
    return updated.map(x => {
      const approvedElsewhere = updated.filter(
        y => y.user_id === x.user_id && y.event_id === x.event_id && y.id !== x.id && y.status === 'accepted'
      ).length;
      return { ...x, already_approved_for_event: approvedElsewhere };
    });
  };

  const update = async (id, status) => {
    try {
      await axios.patch(`/api/agent/applications/${id}`, { status });
      setApps(a => recalcApproved(a, id, status));
      setConflictMsg('');
    } catch (e) {
      // Session V (V-x): was `if (status === 409)`, which swallowed every other
      // server refusal — including D1's new 400 on an invalid status.
      const msg = e.response?.data?.error;
      if (msg) setConflictMsg(msg);
    }
  };

  // Session M2: accept this person across every day of the series they applied
  // to — one action, every guard per day, honest report of days not filled.
  const [seriesReport, setSeriesReport] = useState(null);
  const [seriesBusyId, setSeriesBusyId] = useState(null);
  const acceptSeries = async (id) => {
    setSeriesBusyId(id); setConflictMsg('');
    try {
      const r = await axios.post(`/api/agent/applications/${id}/accept-series`);
      setSeriesReport(r.data);
      await manualLoad();
    } catch (e) {
      setConflictMsg(e.response?.data?.error || 'Could not accept across the series. Please try again.');
    }
    setSeriesBusyId(null);
  };

  const eventNames = [...new Set(apps.map(a => a.event_title))];
  const filtered = filterEvent === 'all' ? apps : apps.filter(a => a.event_title === filterEvent);
  const pending = filtered.filter(a => a.status === 'pending' && a.is_standby === 0);
  const decided = filtered.filter(a => a.status !== 'pending' && a.is_standby === 0);

  // Status colours (16 Jul) — mirrors desktop: green = on the event, danger red
  // = auto-withdrawn (fill/clash), rejected keeps its muted .badge class.
  const statusStyle = s =>
    s === 'accepted' ? { background: 'rgba(22,163,74,0.12)', color: 'var(--green)', border: '1px solid rgba(22,163,74,0.3)' } :
    s === 'withdrawn' ? { background: 'rgba(var(--danger-rgb),0.1)', color: 'var(--danger)', border: '1px solid rgba(var(--danger-rgb),0.3)' } :
    undefined;

  return (
    <div className="screen" style={{ paddingBottom: 80 }}>
      <div className="topbar">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', textAlign: 'left' }}>Applications</h2>
          <button title="Refresh" onClick={manualLoad} disabled={refreshing}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, fontSize: 18, lineHeight: 1, fontFamily: 'var(--font)', opacity: refreshing ? 0.4 : 1 }}>
            {'\u21BB'}
          </button>
        </div>
      </div>

      {conflictMsg && (
        <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: 'rgba(var(--danger-rgb),0.07)', border: '1px solid rgba(var(--danger-rgb),0.25)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
          {conflictMsg}
        </div>
      )}

      {seriesReport && (
        <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: seriesReport.skipped?.length ? 'rgba(202,138,4,0.08)' : 'rgba(22,163,74,0.08)', border: `1px solid ${seriesReport.skipped?.length ? 'rgba(202,138,4,0.4)' : 'rgba(22,163,74,0.3)'}`, borderRadius: 8, fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
            <div>
              {seriesReport.accepted?.length > 0 && <div>✓ <strong>Confirmed:</strong> {seriesReport.accepted.map(x => `Day ${x.day_number} (${x.role})`).join(' · ')}</div>}
              {seriesReport.skipped?.length > 0 && <div style={{ color: '#a16207', marginTop: seriesReport.accepted?.length ? 4 : 0 }}>⚠ <strong>Not filled:</strong> {seriesReport.skipped.map(x => `Day ${x.day_number} — ${x.reason}`).join(' · ')}</div>}
            </div>
            <button onClick={() => setSeriesReport(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: 2 }}>✕</button>
          </div>
        </div>
      )}

      {/* Event filter pills */}
      <div style={{ padding: '0 16px', overflowX: 'auto', display: 'flex', gap: 8, paddingBottom: 12, flexWrap: 'nowrap' }}>
        <button className={`filter-pill ${filterEvent === 'all' ? 'active' : ''}`} onClick={() => setFilterEvent('all')} style={{ whiteSpace: 'nowrap' }}>All</button>
        {eventNames.map(n => (
          <button key={n} className={`filter-pill ${filterEvent === n ? 'active' : ''}`} onClick={() => setFilterEvent(n)} style={{ whiteSpace: 'nowrap' }}>
            {n.length > 18 ? n.slice(0, 18) + '…' : n}
          </button>
        ))}
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <div style={{ padding: '0 16px' }}>

          {/* Pending */}
          {pending.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 10 }}>
                Pending ({pending.length})
              </div>
              {pending.map(a => {
                const alreadyBooked = a.already_approved_for_event > 0;
                return (
                  <div key={a.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 10, opacity: alreadyBooked ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      <MStaffAvatar picture={a.picture} name={a.name} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {a.name}
                          <MInfoButton onClick={() => setInfoApp(a)} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          <strong>{a.role}</strong> · {a.event_title}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {formatDate(a.date)} · {a.shifts_completed > 0 ? `${Number(a.reliability_score).toFixed(1)} (${a.shifts_completed} shifts)` : 'New'}
                        </div>
                        {alreadyBooked && (
                          <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(var(--danger-rgb),0.1)', color: 'var(--danger)', border: '1px solid rgba(var(--danger-rgb),0.25)', fontWeight: 700, display: 'inline-block', marginTop: 4 }}>
                            ALREADY BOOKED
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={() => update(a.id, 'rejected')}
                        style={{ flex: 1, padding: '10px' }}
                      >
                        Reject
                      </button>
                      {alreadyBooked
                        ? <button className="btn btn-sm" disabled style={{ flex: 1, padding: '10px', opacity: 0.4, cursor: 'not-allowed', background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Booked</button>
                        : <button className="btn btn-accept btn-sm" onClick={() => update(a.id, 'accepted')} style={{ flex: 1, padding: '10px' }}>Accept</button>
                      }
                    </div>
                    {/* Session M2: they've applied to other days of this multi-day
                        event too — one tap accepts every day, guards per day */}
                    {!alreadyBooked && Number(a.series_pending_count || 0) > 0 && (
                      <button className="btn btn-accept btn-sm" disabled={seriesBusyId === a.id}
                        onClick={() => acceptSeries(a.id)}
                        style={{ width: '100%', marginTop: 8, padding: '10px' }}>
                        {seriesBusyId === a.id ? 'Accepting…' : `Accept all ${Number(a.series_pending_count || 0) + 1} days of this event`}
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Decided */}
          {decided.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 10, marginTop: 16 }}>
                Confirmed & Rejected
              </div>
              {decided.map(a => (
                <div key={a.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* 16 Jul: same quick-view as the pending cards — photo + eye button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <MStaffAvatar picture={a.picture} name={a.name} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {a.name}
                          <MInfoButton onClick={() => setInfoApp(a)} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{a.role} · {a.event_title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {a.shifts_completed > 0 ? `${Number(a.reliability_score).toFixed(1)} (${a.shifts_completed} shifts)` : 'New'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      <span className={`badge badge-${a.status}`} style={statusStyle(a.status)}>{a.status}</span>
                      {/* Session AM: right-aligned under the pill, matching the
                          column this card already uses. */}
                      {statusAge(a.status_changed_at) && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: -2 }}>{statusAge(a.status_changed_at)}</div>}
                      {a.status === 'accepted' && <button className="btn btn-reject btn-sm" onClick={() => update(a.id, 'rejected')}>Remove</button>}
                      {a.status === 'rejected' && <button className="btn btn-ghost btn-sm" onClick={() => update(a.id, 'pending')} style={{ fontSize: 11 }}>↩ Reinstate</button>}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {apps.length === 0 && <div className="empty-state">No applications yet.</div>}
        </div>
      )}
      {infoApp && <MApplicantInfoFlash app={infoApp} onClose={() => setInfoApp(null)} />}
    </div>
  );
}

// ── Events tab ────────────────────────────────────────────────────────────────
// ── BOSSE Step 3: on-site attendance roster (mobile-first — phones at venues) ──
// Manager mark is the authoritative record for pay (Step 4). Self-check-in shows
// as an indicator only. Tap the active state again to clear a mis-tap.
// Step 3.5 Pass A: plain text labels (no icons) — compact rows for venue use.
const M_ATT = {
  arrived: { label: 'Arrived', color: '#16a34a', bg: 'rgba(22,163,74,0.12)', border: 'rgba(22,163,74,0.5)' },
  late:    { label: 'Late',    color: '#ca8a04', bg: 'rgba(202,138,4,0.12)',  border: 'rgba(202,138,4,0.5)' },
  no_show: { label: 'No-show', color: '#dc2626', bg: 'rgba(220,38,38,0.12)',  border: 'rgba(220,38,38,0.5)' },
};

function MobileRosterRow({ row, canMark, onChange, onNoteFocus, onNoteBlur, onUnsettle }) {
  const [busy, setBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState(row.attendance_note || '');
  const [noteSaving, setNoteSaving] = useState(false);
  const noteFocusedRef = useRef(false);

  // Step 3.5 Pass A: follow another manager's note landing via auto-refresh —
  // but never while THIS manager is typing (parent skips the row on merge too).
  useEffect(() => {
    if (!noteFocusedRef.current) setNoteDraft(row.attendance_note || '');
  }, [row.attendance_note]);

  // Session R: lock on money, not on clock. The roster stays open after the
  // event ends; a row freezes only once that shift is settled in Payroll (the
  // server 409s on the same condition). Un-settling re-opens it.
  const locked = row.paid === 1 || row.paid === true;
  const editable = canMark && !locked;

  const mark = async (value) => {
    if (!editable || busy) return;
    const next = row.attendance === value ? null : value;
    const note = next === 'no_show' ? (row.attendance_note || 'Did not show up')
               : next === null ? null : (row.attendance_note || null);
    setBusy(true);
    try {
      const r = await axios.patch(`/api/agent/applications/${row.application_id}/attendance`, { attendance: next, attendance_note: note });
      onChange(row.application_id, { attendance: r.data.attendance, attendance_note: r.data.attendance_note });
      setNoteDraft(r.data.attendance_note || '');
    } catch (e) {
      alert(e.response?.data?.error || 'Could not update attendance');
    }
    setBusy(false);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const r = await axios.patch(`/api/agent/applications/${row.application_id}/attendance`, { attendance: row.attendance, attendance_note: noteDraft.trim() || null });
      onChange(row.application_id, { attendance_note: r.data.attendance_note });
      // 17 Jul: confirm the save visibly — same ✓ Saved flash as adjustments
      setNoteSaving('saved');
      setTimeout(() => setNoteSaving(false), 2000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save note');
      setNoteSaving(false);
    }
  };

  const showNote = row.attendance === 'late' || row.attendance === 'no_show';
  // Step 3.5 Pass A: one dense row — name left, three text buttons right, so
  // roughly twice as many staff fit per screen. Tap targets stay ≥44px.
  return (
    <div style={{ padding: '5px 8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', flexShrink: 0 }}>
          {row.picture ? <img src={row.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (row.name || 'S').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.role}
            {row.checked_in ? <span style={{ color: '#16a34a' }}>{' \u00B7 self \u2713'}{row.checked_in_at ? ` ${new Date(row.checked_in_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}</span> : ''}
            {row.phone && <>{' \u00B7 '}<a href={`tel:${row.phone}`} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Call</a></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
          {/* Session R: settled = frozen. The mark stays visible; the buttons go. */}
          {locked ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 10px', borderRadius: 8, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
              background: 'var(--bg-surface)',
              border: `1.5px solid ${row.attendance && M_ATT[row.attendance] ? M_ATT[row.attendance].border : 'var(--border)'}`,
              color: row.attendance && M_ATT[row.attendance] ? M_ATT[row.attendance].color : 'var(--text-secondary)' }}>
              {'\u{1F512}'} {row.attendance && M_ATT[row.attendance] ? M_ATT[row.attendance].label : 'Unmarked'}
            </span>
          ) : ['arrived', 'late', 'no_show'].map(v => {
            const s = M_ATT[v];
            const active = row.attendance === v;
            return (
              <button key={v} onClick={() => mark(v)} disabled={!editable || busy}
                style={{ minHeight: 44, padding: '0 8px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: editable ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)',
                  background: active ? s.bg : 'transparent',
                  border: `1.5px solid ${active ? s.border : 'var(--border)'}`,
                  color: active ? s.color : 'var(--text-secondary)',
                  opacity: editable ? 1 : 0.5 }}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Session R: settled rows unlock through a deliberate un-settle. */}
      {locked && onUnsettle && (
        <button onClick={() => onUnsettle(row)}
          style={{ width: '100%', minHeight: 38, marginTop: 6, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font)', cursor: 'pointer' }}>
          Settled — un-settle to edit
        </button>
      )}
      {showNote && locked && row.attendance_note && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-secondary)', fontStyle: 'italic' }}>Note: {row.attendance_note}</div>
      )}
      {showNote && !locked && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 3 }}>
          <input value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
            onFocus={() => { noteFocusedRef.current = true; onNoteFocus && onNoteFocus(row.application_id); }}
            onBlur={() => { noteFocusedRef.current = false; onNoteBlur && onNoteBlur(row.application_id); }}
            placeholder={row.attendance === 'late' ? 'Note (optional) — e.g. "50 min late"' : 'Note'}
            style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font)' }} />
          <button onClick={saveNote} disabled={!!noteSaving || !editable}
            style={{ padding: '0 14px', borderRadius: 10, border: 'none',
              background: noteSaving === 'saved' ? 'rgba(22,163,74,0.12)' : 'var(--accent)',
              color: noteSaving === 'saved' ? '#16a34a' : '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)' }}>
            {noteSaving === 'saved' ? '✓' : noteSaving ? '…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

function MobileRoster({ event, ended, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // Step 3.5 Pass A
  // Step 3.5 Pass A: rows whose note editor is focused by THIS manager — skipped
  // on merge so auto-refresh never wipes a half-typed note.
  const noteEditingRef = useRef(new Set());

  const fetchRows = () =>
    axios.get(`/api/agent/events/${event.id}/checkins`)
      .then(r => setRows(prev => r.data.map(f =>
        noteEditingRef.current.has(f.application_id)
          ? (prev.find(p => p.application_id === f.application_id) || f)
          : f
      )))
      .catch(() => {});

  useEffect(() => {
    axios.get(`/api/agent/events/${event.id}/checkins`)
      .then(r => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
    // Step 3.5 Pass A: multiple managers mark from different gates — poll every
    // 20s while the roster is open and merge marks in.
    const iv = setInterval(fetchRows, 20000);
    return () => clearInterval(iv);
  }, [event.id]);

  const manualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await fetchRows();
    setRefreshing(false);
  };

  const canMark = Date.now() >= new Date(`${event.date}T${event.time_start || '00:00'}`).getTime() - 60 * 60 * 1000;
  const sync = (appId, fields) => setRows(rs => rs.map(r => r.application_id === appId ? { ...r, ...fields } : r));
  const count = v => rows.filter(r => r.attendance === v).length;
  const unmarked = rows.filter(r => !r.attendance).length;

  // Session R: the escape hatch the settlement lock requires. Re-opens ONE
  // person's shift so their attendance can be corrected; it returns to
  // Outstanding in Payroll and must be settled again.
  const unsettleOne = async (row) => {
    if (!window.confirm(
      `Un-settle ${row.name}'s shift?\n\n`
      + `This unlocks their attendance so you can correct it. The shift returns to Outstanding in Payroll and must be settled again once it is right.\n\n`
      + `Money already paid into their account is NOT reversed by this — that stays a bank matter.`
    )) return;
    try {
      await axios.patch('/api/agent/payroll/unsettle', { application_ids: [row.application_id] });
      sync(row.application_id, { paid: 0, paid_at: null });
    } catch (e) {
      alert(e.response?.data?.error || 'Could not un-settle this shift.');
    }
  };


  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-main, var(--bg-surface))', zIndex: 60, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Roster — {event.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{formatDate(event.date)} · {event.time_start}–{event.time_end}{ended ? ' · ended' : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {/* Step 3.5 Pass A: manual refresh (auto-refresh runs every 20s) */}
          <button onClick={manualRefresh} disabled={refreshing} title="Refresh roster"
            style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, lineHeight: 1, opacity: refreshing ? 0.4 : 1 }}>↻</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? <div className="loading">Loading roster...</div> : rows.length === 0 ? (
          <div className="empty-state">No confirmed staff for this event yet.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[['arrived', 'Arrived', '#16a34a'], ['late', 'Late', '#ca8a04'], ['no_show', 'No-show', '#dc2626']].map(([v, label, color]) => (
                <div key={v} style={{ flex: 1, textAlign: 'center', padding: '8px 0', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: count(v) > 0 ? color : 'var(--text-muted)' }}>{count(v)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</div>
                </div>
              ))}
              <div style={{ flex: 1, textAlign: 'center', padding: '8px 0', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{unmarked}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Unmarked</div>
              </div>
            </div>
            {!canMark && (
              <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(202,138,4,0.08)', border: '1px solid rgba(202,138,4,0.3)', borderRadius: 10, fontSize: 12, color: '#ca8a04' }}>
                Roster opens 1 hour before the shift starts.
              </div>
            )}
            {/* Session R: ended events stay editable — settlement is the only lock. */}
            {ended && (
              <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.28)', borderRadius: 10, fontSize: 12, color: '#2563eb' }}>
                This event has ended — attendance is still editable. A row locks only once that shift is settled in Payroll.
              </div>
            )}
            {ended && unmarked > 0 && rows.some(r => !r.attendance && !(r.paid === 1 || r.paid === true)) && (
              <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(202,138,4,0.08)', border: '1px solid rgba(202,138,4,0.3)', borderRadius: 10, fontSize: 12, color: '#ca8a04' }}>
                ⚠ <strong>{unmarked} of {rows.length} not marked.</strong> Unmarked staff are paid in full — reconcile before you export the bank file.
              </div>
            )}
            {rows.map(r => <MobileRosterRow key={r.application_id} row={r} canMark={canMark} onChange={sync}
              onUnsettle={unsettleOne}
              onNoteFocus={id => noteEditingRef.current.add(id)}
              onNoteBlur={id => noteEditingRef.current.delete(id)} />)}
          </>
        )}
      </div>

    </div>
  );
}

// ── Series card (Session M1, mobile) ─────────────────────────────────────────
// Mirror of the desktop SeriesGroupCard: groups the day-events of one
// multi-day series under a single expandable card. Collapsed: per-day
// staffing rows; expanded: every day rendered as its normal full EventCard,
// so publish / delete / roster / exports work per day exactly as on a
// standalone event. Module-level so the open state survives the parent's
// 20-second background refresh.
function MSeriesCard({ days, renderDay, onPublishSeries, seriesBusy }) {
  const [open, setOpen] = useState(false);
  const first = days[0];
  const last = days[days.length - 1];
  const pubCount = days.filter(d => d.published).length;
  const allPublished = pubCount === days.length;
  const totFilled = days.reduce((a, d) => a + (d.slots_filled || 0), 0);
  const totSlots = days.reduce((a, d) => a + (d.slots_total || 0), 0);
  const endedDays = Math.max(0, (first.series_size || days.length) - days.length);
  const busy = seriesBusy === (first.parent_event_id || first.id);
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: '3px solid var(--blue,#2563eb)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div onClick={() => setOpen(v => !v)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>{first.title}</span>
          <span className="badge badge-series" style={{ fontSize: 9 }}>MULTI-DAY</span>
          {pubCount === 0 && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(217,119,6,0.15)', color: 'var(--amber)', border: '1px solid rgba(217,119,6,0.3)', fontWeight: 700 }}>DRAFT</span>}
          {pubCount > 0 && !allPublished && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(217,119,6,0.15)', color: 'var(--amber)', border: '1px solid rgba(217,119,6,0.3)', fontWeight: 700 }}>{pubCount}/{days.length} PUBLISHED</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>
          {formatDate(first.date)} – {formatDate(last.date)} · {days.length} day{days.length > 1 ? 's' : ''} upcoming{endedDays > 0 ? ` (${endedDays} ended)` : ''}<br />
          {first.location}
        </div>

        {/* Per-day staffing overview */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
          {days.map(d => {
            const pct = fillPercent(d.slots_filled, d.slots_total);
            return (
              <div key={d.id} style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, marginBottom: 2 }}>
                  <span style={{ color: 'var(--text-primary)' }}>
                    <strong>Day {d.day_number}</strong>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{formatDate(d.date)} · {d.time_start}–{d.time_end}</span>
                    {!d.published && <span style={{ fontSize: 9, color: 'var(--amber)', fontWeight: 700, marginLeft: 6 }}>DRAFT</span>}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>{d.slots_filled || 0}/{d.slots_total || 0}</span>
                </div>
                <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: fillColor(d.slots_filled, d.slots_total), borderRadius: 2, transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-light)' }}>
            <span>Whole event</span>
            <span><strong style={{ color: totFilled >= totSlots && totSlots > 0 ? 'var(--green)' : 'var(--text-primary)' }}>{totFilled}/{totSlots}</strong> filled</span>
          </div>
        </div>
      </div>

      {/* Series actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => onPublishSeries(first, !allPublished, days.length)}
          disabled={busy}
          className="btn btn-sm"
          style={{ flex: 1, padding: '9px', background: allPublished ? 'rgba(var(--danger-rgb),0.08)' : 'rgba(22,163,74,0.08)', color: allPublished ? 'var(--danger)' : 'var(--green)', border: `1px solid ${allPublished ? 'rgba(var(--danger-rgb),0.25)' : 'rgba(22,163,74,0.3)'}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
        >
          {busy ? 'Working…' : allPublished ? 'Unpublish all days' : 'Publish all days'}
        </button>
        <button
          onClick={() => setOpen(v => !v)}
          className="btn btn-sm"
          style={{ flex: 1, padding: '9px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', cursor: 'pointer' }}
        >
          {open ? '▲ Collapse days' : '▼ Manage days'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {days.map(d => renderDay(d))}
        </div>
      )}
    </div>
  );
}

function MobileEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [rosterEvent, setRosterEvent] = useState(null); // BOSSE Step 3
  // Session AK: the event being previewed exactly as staff see it, or null.
  const [previewEvent, setPreviewEvent] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // My Events auto-refresh (16 Jul)

  const load = () => axios.get('/api/events').then(r => setEvents(r.data));
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  // My Events auto-refresh (16 Jul) — same pattern as desktop: silent 20s
  // refresh so check-in tallies and staffing bars stay live, paused while the
  // roster panel or delete confirm is open (their close paths already reload).
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.hidden) return;
      if (rosterEvent || deleteConfirm) return;
      load();
    }, 20000);
    return () => clearInterval(iv);
  }, [rosterEvent, deleteConfirm]);

  const manualLoad = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const now = new Date();
  const eventEnd = e => {
    try {
      const start = e.time_start || '00:00';
      const end = e.time_end || '23:59';
      const d = new Date(`${e.date}T${end}`);
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      if ((eh * 60 + em) < (sh * 60 + sm)) d.setDate(d.getDate() + 1); // overnight — ends next day
      return d;
    } catch { return new Date(e.date); }
  };

  const upcomingEvents = events.filter(e => { try { return eventEnd(e) >= now; } catch { return true; } })
    .sort((a, b) => new Date(`${a.date}T${a.time_start || '00:00'}`) - new Date(`${b.date}T${b.time_start || '00:00'}`));
  const pastEvents = events.filter(e => { try { return eventEnd(e) < now; } catch { return false; } })
    .sort((a, b) => eventEnd(b) - eventEnd(a));

  const togglePublish = async (e) => {
    // Session C: unpublish needs a guard — one accidental click hid an event
    // from staff. Publish stays one-click.
    if (e.published && !window.confirm('Unpublish this event? Staff who have applied will no longer see it, and no new applications can come in until it is published again.')) return;
    await axios.patch(`/api/events/${e.id}/publish`, { published: !e.published });
    setEvents(ev => ev.map(x => x.id === e.id ? { ...x, published: x.published ? 0 : 1 } : x));
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/events/${deleteConfirm.id}`);
    } catch (e) {
      // Session B: the server now refuses to delete ended events with confirmed
      // staff (payroll/rating history) — show its message instead of freezing.
      alert(e.response?.data?.error || 'Could not delete the event. Please try again.');
    }
    setDeleteConfirm(null);
    setDeleting(false);
    load();
  };

  // Session M1: whole-series publish/unpublish — one action across every day.
  // Unpublish keeps the Session C guard; publish stays one-click.
  const [seriesBusy, setSeriesBusy] = useState(null);
  const publishSeries = async (anchor, publish, dayCount) => {
    if (!publish && !window.confirm(`Unpublish all ${dayCount} days of this event? Staff who have applied will no longer see any of them, and no new applications can come in until they are published again.`)) return;
    setSeriesBusy(anchor.parent_event_id || anchor.id);
    try {
      await axios.patch(`/api/events/${anchor.id}/publish-series`, { published: publish });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not update the series. Please try again.');
    }
    setSeriesBusy(null);
    await load();
  };

  // Session M1: group upcoming day-events into series (server-computed
  // series_size covers the FULL series). Ended days fall into Past Events as
  // normal per-day cards — a day IS an event.
  const groupedUpcoming = (() => {
    const out = []; const byKey = new Map();
    for (const e of upcomingEvents) {
      if ((e.series_size || 1) > 1) {
        const k = e.parent_event_id || e.id;
        if (!byKey.has(k)) { const g = { type: 'series', key: k, days: [] }; byKey.set(k, g); out.push(g); }
        byKey.get(k).days.push(e);
      } else out.push({ type: 'single', e });
    }
    return out;
  })();

  const EventCard = ({ e, isPast }) => {
    const totalFilled = e.roles?.reduce((s, r) => s + (r.slots_filled || 0), 0) || 0;
    const totalSpots = e.roles?.reduce((s, r) => s + (r.spots_available || 0), 0) || 0;
    const fillPct = fillPercent(totalFilled, totalSpots);
    const [exporting, setExporting] = useState(false); // BOSSE Step 2

    // BOSSE Step 2: authenticated xlsx download (JWT rides the axios header)
    const exportStaffInfo = async () => {
      setExporting(true);
      try {
        const r = await axios.get(`/api/agent/events/${e.id}/staff-export`, { responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = (`Staff - ${e.title} - ${e.date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'staff-export') + '.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        alert('Could not export staff info. Make sure at least one applicant is accepted.');
      }
      setExporting(false);
    };

    // Session P: past-event shortcut into the same bank export the desktop
    // Payroll page uses — one implementation, two entry points.
    const exportPayments = async () => {
      setExporting(true);
      try {
        const r = await axios.get(`/api/agent/events/${e.id}/payment-export?format=csv`, { responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = (`Payments - ${e.title}${(e.series_size||1)>1?` - Day ${e.day_number}`:''} - ${e.date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'payment-export') + '.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Could not generate the payment export.';
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
        alert(msg);
      }
      setExporting(false);
    };

    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>{e.title}</span>
              {(e.series_size || 1) > 1 && <span className="badge badge-series" style={{ fontSize: 9 }}>DAY {e.day_number}/{e.series_size}</span>}
              {!e.published && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(217,119,6,0.15)', color: 'var(--amber)', border: '1px solid rgba(217,119,6,0.3)', fontWeight: 700 }}>DRAFT</span>}
              {/* Session AK: preview what staff see. No stopPropagation needed
                  here — unlike the desktop card, this title block has no
                  onClick of its own. */}
              <button
                onClick={() => setPreviewEvent(e)}
                style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
              >ⓘ Preview</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {formatDate(e.date)} · {e.time_start}–{e.time_end}<br />
              {e.location}
            </div>
          </div>
        </div>

        {/* Fill rate bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>Staffing</span>
            <span>{totalFilled}/{totalSpots} filled</span>
          </div>
          <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${fillPct}%`, background: fillColor(totalFilled, totalSpots), borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Roles */}
        {e.roles?.map(r => {
          const perStaff = r.pay_unit === 'per_staff';
          const cost = isPast && !perStaff ? mEarned(r.pay_amount, r.pay_unit, e.time_start, e.time_end) : null;
          const payable = Math.max(0, (r.slots_filled || 0) - (r.no_show_count || 0)); // BOSSE Step 4: no-shows excluded
          const roleTotal = cost && cost.earned != null ? cost.earned * payable : null;
          return (
            <div key={r.id} style={{ padding: '6px 0', borderTop: '1px solid var(--border-light)', fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{r.role}</span>
                <span style={{ color: 'var(--text-muted)' }}>{r.slots_filled}/{r.spots_available}</span>
              </div>
              {isPast && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                  {perStaff ? (
                    <>
                      <span>Per staff</span>
                      <span>·</span>
                      <span>see Payroll</span>
                      <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontWeight: 700 }}>—</span>
                    </>
                  ) : (
                    <>
                      <span>{r.pay_rate || mRateStr(r.pay_amount, r.pay_unit) || '—'}</span>
                      <span>·</span>
                      <span>{r.pay_unit === 'daily' ? '1 day' : r.pay_unit === 'per_event' ? 'Flat' : (cost && cost.hours != null ? `${fmtH(cost.hours)}h` : '—')}</span>
                      <span style={{ marginLeft: 'auto', color: 'var(--green)', fontWeight: 700 }}>
                        {roleTotal != null ? `R${fmtM(roleTotal)}` : '—'}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Payroll step 2: event cost total (past events only) */}
        {isPast && (() => {
          let total = 0, partial = false;
          (e.roles || []).forEach(r => {
            if (r.pay_unit === 'per_staff') { if ((r.slots_filled || 0) > 0) partial = true; return; }
            const c = mEarned(r.pay_amount, r.pay_unit, e.time_start, e.time_end);
            if (c.earned != null) total += c.earned * Math.max(0, (r.slots_filled || 0) - (r.no_show_count || 0)); // BOSSE Step 4
            else if ((r.slots_filled || 0) > 0) partial = true;
          });
          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Event total</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--green)' }}>R{fmtM(total)}{partial ? '+' : ''}</span>
            </div>
          );
        })()}

        {/* Step 3.5 Pass A: two entry points, side by side. "Check-in" (24h before
            start → event end, self-check-in tally) and "Live Roster" (1h before
            start → event end, attendance-marked tally). Both open the same roster
            view — the labels, tallies, and windows are the differentiation. */}
        {!isPast && totalFilled > 0 && (() => {
          const startMs = new Date(`${e.date}T${e.time_start || '00:00'}`).getTime();
          const showCheckin = Date.now() >= startMs - 24 * 60 * 60 * 1000; // card itself only renders until event end
          const showRoster = Date.now() >= startMs - 60 * 60 * 1000;
          if (!showCheckin && !showRoster) return null;
          return (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {showCheckin && (
                <button
                  onClick={() => setRosterEvent(e)}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'center' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>Check-in</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                    <span style={{ fontWeight: 700, color: e.checkin_count > 0 ? '#16a34a' : 'var(--text-primary)' }}>{e.checkin_count || 0}</span>/{totalFilled} self-checked-in
                  </div>
                </button>
              )}
              {showRoster && (
                <button
                  onClick={() => setRosterEvent(e)}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 10, border: '1.5px solid rgba(22,163,74,0.4)', background: 'rgba(22,163,74,0.08)', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'center' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#16a34a' }}>Live Roster</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                    <span style={{ fontWeight: 700, color: e.attendance_marked_count > 0 ? '#16a34a' : 'var(--text-primary)' }}>{e.attendance_marked_count || 0}</span>/{totalFilled} marked
                  </div>
                </button>
              )}
            </div>
          );
        })()}

        {/* Session R: rosters stay reachable after the event ends — same
            controls as the live roster, settled rows locked. The unmarked count
            is a NUDGE, never a block: unmarked staff are paid in full. */}
        {isPast && totalFilled > 0 && (() => {
          const marked = e.attendance_marked_count || 0;
          const unmarked = Math.max(0, totalFilled - marked);
          // Only nag while at least one unmarked row is still unsettled — once
          // they are settled the gap can no longer be closed, so the amber goes.
          const stillFixable = (e.unmarked_open_count || 0) > 0;
          return (
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => setRosterEvent(e)}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'center' }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>View / Edit Roster</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                  <span style={{ fontWeight: 700, color: marked > 0 ? '#16a34a' : 'var(--text-primary)' }}>{marked}</span>/{totalFilled} marked
                </div>
              </button>
              {unmarked > 0 && stillFixable && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(202,138,4,0.08)', border: '1px solid rgba(202,138,4,0.3)', borderRadius: 10, fontSize: 11.5, color: '#ca8a04' }}>
                  ⚠ {unmarked} of {totalFilled} not marked — unmarked staff are paid in full.
                </div>
              )}
            </div>
          );
        })()}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {!isPast && (
            <button
              onClick={exportStaffInfo}
              disabled={totalFilled === 0 || exporting}
              className="btn btn-sm"
              title={totalFilled > 0 ? 'Download an xlsx of accepted staff' : 'No accepted staff yet'}
              style={{ flex: 1, padding: '9px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 700, color: totalFilled > 0 ? 'var(--text-primary)' : 'var(--text-muted)', cursor: totalFilled > 0 ? 'pointer' : 'not-allowed', opacity: totalFilled > 0 ? 1 : 0.55 }}
            >
              {exporting ? 'Exporting…' : 'Export staff'}
            </button>
          )}
          {/* Session P: bank payment export on past events */}
          {isPast && (
            <button
              onClick={exportPayments}
              disabled={totalFilled === 0 || exporting}
              className="btn btn-sm"
              title={totalFilled > 0 ? 'Download the bank payment CSV' : 'No accepted staff'}
              style={{ flex: 1, padding: '9px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 700, color: totalFilled > 0 ? 'var(--text-primary)' : 'var(--text-muted)', cursor: totalFilled > 0 ? 'pointer' : 'not-allowed', opacity: totalFilled > 0 ? 1 : 0.55 }}
            >
              {exporting ? 'Exporting…' : '🏦 Payments CSV'}
            </button>
          )}
          <button
            onClick={() => togglePublish(e)}
            className="btn btn-sm"
            style={{ flex: 1, padding: '9px', background: e.published ? 'rgba(var(--danger-rgb),0.08)' : 'rgba(22,163,74,0.08)', color: e.published ? 'var(--danger)' : 'var(--green)', border: `1px solid ${e.published ? 'rgba(var(--danger-rgb),0.25)' : 'rgba(22,163,74,0.3)'}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            {e.published ? 'Unpublish' : 'Publish'}
          </button>
          <button
            onClick={() => setDeleteConfirm(e)}
            className="btn btn-sm"
            style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="screen" style={{ paddingBottom: 80 }}>
      <div className="topbar">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', textAlign: 'left' }}>My Events</h2>
          {/* Manual refresh — auto-refresh runs every 20s */}
          <button title="Refresh events" onClick={manualLoad} disabled={refreshing}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, fontSize: 18, lineHeight: 1, fontFamily: 'var(--font)', opacity: refreshing ? 0.4 : 1 }}>
            {'\u21BB'}
          </button>
        </div>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <div style={{ padding: '0 16px' }}>
          {upcomingEvents.length === 0 && <div className="empty-state">No upcoming events.</div>}
          {/* Session M1: standalone events render as before; multi-day series
              group under one expandable card whose expanded view reuses the
              normal per-day EventCard. */}
          {groupedUpcoming.map(item => item.type === 'single'
            ? <EventCard key={item.e.id} e={item.e} />
            : <MSeriesCard key={`series-${item.key}`} days={item.days} seriesBusy={seriesBusy} onPublishSeries={publishSeries} renderDay={(d) => <EventCard key={d.id} e={d} />} />)}

          {/* Past events collapsible */}
          {pastEvents.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setShowPast(v => !v)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}
              >
                <span>Past Events ({pastEvents.length})</span>
                <span style={{ fontSize: 11, transform: showPast ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s' }}>▼</span>
              </button>
              {showPast && (
                <div style={{ marginTop: 8 }}>
                  {pastEvents.map(e => <EventCard key={e.id} e={e} isPast />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* BOSSE Step 3: full-screen attendance roster */}
      {/* Session R: the same roster for live and past events — `ended` only
          changes the wording and which nudges show. Reload on close so the
          card's "N marked" tally and unmarked nudge catch up immediately. */}
      {rosterEvent && <MobileRoster event={rosterEvent} ended={eventEnd(rosterEvent) < new Date()}
        onClose={() => { setRosterEvent(null); load(); }} />}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 360 }}>
            <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8, color: 'var(--text-primary)' }}>Delete Event?</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              All confirmed and pending staff will receive a cancellation notification and the event will be removed.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: 'var(--danger)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Session AK: same shared component as desktop and Book a Shift. */}
      <EventDetailSheet event={previewEvent} onClose={() => setPreviewEvent(null)} previewNote />
    </div>
  );
}

// ── Staff tab ─────────────────────────────────────────────────────────────────
function MobileStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);

  useEffect(() => {
    axios.get('/api/agent/staff').then(r => setStaff(r.data)).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    await axios.patch(`/api/agent/staff/${id}/status`, { status });
    setStaff(s => s.map(x => x.id === id ? { ...x, status } : x));
  };

  const generateInviteLink = async () => {
    const { data } = await axios.post('/api/agent/invite-code');
    setInviteLink(`${window.location.origin}/join/${data.code}`);
  };
  // Session B: new code, old link dies instantly — the leaked-link escape hatch
  const regenerateInviteLink = async () => {
    if (!window.confirm('Generate a new invite link? The current link will stop working immediately — anyone still holding it will not be able to register.')) return;
    try {
      const { data } = await axios.post('/api/agent/invite-code/regenerate');
      setInviteLink(`${window.location.origin}/join/${data.code}`);
      setInviteCopied(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not regenerate the invite link. Please try again.');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const filtered = (filter === 'all' ? staff : staff.filter(s => s.status === filter))
    .filter(s => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
    });

  return (
    <div className="screen" style={{ paddingBottom: 80 }}>
      <div className="topbar">
        <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' }}>Staff Portal</h2>
      </div>

      <div style={{ padding: '0 16px 12px' }}>
        {/* Invite Link */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Staff Invite Link</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Share this link with staff. They'll register and land in your pending queue.</div>
          {!inviteLink ? (
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '8px 14px', width: '100%' }} onClick={generateInviteLink}>Generate Invite Link</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input readOnly value={inviteLink} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-secondary)', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }} />
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '8px 14px' }} onClick={copyLink}>
                {inviteCopied ? '✓ Copied!' : 'Copy Link'}
              </button>
              <button onClick={regenerateInviteLink} style={{ fontSize: 12, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                ↻ Regenerate Link
              </button>
            </div>
          )}
        </div>

        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, marginBottom: 10, boxSizing: 'border-box', fontFamily: 'var(--font)' }}
        />
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flexWrap: 'nowrap' }}>
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button key={f} className={`filter-pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)} style={{ whiteSpace: 'nowrap' }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <div style={{ padding: '0 16px' }}>
          {filtered.length === 0 && <div className="empty-state">No staff in this category.</div>}
          {filtered.map(s => (
            <div key={s.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{s.email}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {s.shifts_completed > 0 ? `${Number(s.reliability_score).toFixed(1)} · ${s.shifts_completed} shift${s.shifts_completed === 1 ? '' : 's'}` : 'New'}
                  </div>
                  {s.skills && (
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {s.skills.split(',').map(sk => sk.trim()).filter(Boolean).map(sk => (
                        <span key={sk} className="badge badge-info" style={{ fontSize: 9 }}>{sk}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className={`badge badge-${s.status}`}>{s.status}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {s.status !== 'approved' && <button className="btn btn-accept btn-sm" onClick={() => updateStatus(s.id, 'approved')} style={{ flex: 1, padding: '9px' }}>Approve</button>}
                {s.status !== 'pending' && <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(s.id, 'pending')} style={{ flex: 1, padding: '9px' }}>Hold</button>}
                {s.status !== 'rejected' && <button className="btn btn-reject btn-sm" onClick={() => updateStatus(s.id, 'rejected')} style={{ flex: 1, padding: '9px' }}>Reject</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Comms tab ─────────────────────────────────────────────────────────────────
// Sent-history message rendering: overflow fix + 3-line clamp + linkified URLs
// — mirrors the staff Updates feed and the desktop sent history.
const M_URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const mLinkify = text => String(text || '').split(M_URL_RE).map((part, i) => {
  if (i % 2 === 1) {
    const href = part.startsWith('www.') ? `https://${part}` : part;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'underline' }}>{part}</a>;
  }
  return part;
});

function MUpdateMessage({ text, style }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) setClamped(ref.current.scrollHeight > ref.current.clientHeight + 1);
  }, [text]);
  return (
    <>
      <div ref={ref}
        onClick={() => { if (clamped && !expanded) setExpanded(true); }}
        style={{
          ...style,
          overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
          cursor: clamped && !expanded ? 'pointer' : 'default',
          ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}>
        {mLinkify(text)}
      </div>
      {(clamped || expanded) && (
        <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, fontSize: 12, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit' }}>
          {expanded ? 'Show less' : 'Read more…'}
        </button>
      )}
    </>
  );
}

function MobileComms() {
  const [form, setForm] = useState({ title: '', message: '', type: 'info' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [sendToAll, setSendToAll] = useState(true);
  const [sendToEvent, setSendToEvent] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [sentUpdates, setSentUpdates] = useState([]);
  const [showSent, setShowSent] = useState(false);
  // Session S: SMS nudge is an explicit opt-in with a visible recipient count
  const [smsNudge, setSmsNudge] = useState(false);
  const [smsCount, setSmsCount] = useState(null);
  // Session M2: send to every day of a multi-day event in one action
  const [wholeSeries, setWholeSeries] = useState(false);

  useEffect(() => {
    axios.get('/api/events').then(r => setEvents(r.data));
    axios.get('/api/agent/updates/sent').then(r => setSentUpdates(r.data));
  }, []);

  // Recount on target change; untick on any audience change so a previous
  // decision never silently carries over.
  useEffect(() => {
    setSmsNudge(false);
    if (sendToEvent && selectedEvent) {
      setSmsCount(null);
      axios.post('/api/agent/sms/preview', { audience: { type: 'event', event_id: parseInt(selectedEvent), series: wholeSeries } })
        .then(r => setSmsCount(r.data.count))
        .catch(() => setSmsCount(null));
    } else {
      setSmsCount(null);
    }
  }, [sendToEvent, selectedEvent, wholeSeries]);

  // Session M2: untick "all days" whenever the target changes
  useEffect(() => { setWholeSeries(false); }, [selectedEvent, sendToEvent]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSendToAll = () => { setSendToAll(true); setSendToEvent(false); setSelectedEvent(''); };
  const handleSendToEvent = () => { setSendToEvent(true); setSendToAll(false); };

  const submit = async e => {
    e.preventDefault();
    if (sendToEvent && !selectedEvent) return alert('Please select an event.');
    setSaving(true);
    const payload = { ...form, ...(sendToEvent ? { event_id: selectedEvent, send_sms: smsNudge, series: wholeSeries } : {}) };
    await axios.post('/api/agent/updates', payload);
    axios.get('/api/agent/updates/sent').then(r => setSentUpdates(r.data));
    setSaving(false); setDone(true);
    setForm({ title: '', message: '', type: 'info' });
    setSelectedEvent(''); setSendToAll(true); setSendToEvent(false); setSmsNudge(false); setWholeSeries(false);
    setTimeout(() => setDone(false), 3000);
  };

  return (
    <div className="screen" style={{ paddingBottom: 80 }}>
      <div className="topbar">
        <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' }}>Send Update</h2>
      </div>

      <div style={{ padding: '0 16px' }}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Sends an in-app update — no SMS unless you tick the SMS box on an event update.
        </p>
        {done && (
          <div style={{ padding: 12, background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: 8, color: 'var(--green)', marginBottom: 16, fontSize: 13 }}>
            ✓ Update sent.
          </div>
        )}

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div style={{ marginBottom: 14 }}>
            <label className="form-label">Type</label>
            <select className="form-select" value={form.type} onChange={set('type')}>
              <option value="info">General Info</option>
              <option value="event">Event Brief Change</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="form-label">Title</label>
            <input className="form-input" placeholder="e.g. Uniform policy update" value={form.title} onChange={set('title')} required />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="form-label">Message</label>
            <textarea className="form-textarea" rows={4} value={form.message} onChange={set('message')} required style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>

          {/* Audience */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Send to</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSendToAll}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${sendToAll ? 'var(--accent)' : 'var(--border)'}`, background: sendToAll ? 'var(--accent-glow)' : 'transparent', color: sendToAll ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                All Staff
              </button>
              <button
                type="button"
                onClick={handleSendToEvent}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${sendToEvent ? 'var(--accent)' : 'var(--border)'}`, background: sendToEvent ? 'var(--accent-glow)' : 'transparent', color: sendToEvent ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Event Only
              </button>
            </div>
            {sendToEvent && (
              <select className="form-select" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} style={{ marginTop: 10 }} required={sendToEvent}>
                <option value="">Select an event...</option>
                {events.filter(e => { const today = new Date().toISOString().slice(0, 10); return e.date >= today; }).map(e => <option key={e.id} value={e.id}>{e.title} — {formatDate(e.date)}</option>)}
              </select>
            )}
            {/* Session M2: multi-day events can target every day at once —
                one update per person across the whole series */}
            {sendToEvent && selectedEvent && (() => { const sel = events.find(e => String(e.id) === String(selectedEvent)); return sel && (sel.series_size || 1) > 1; })() && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={wholeSeries} onChange={e => setWholeSeries(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                <span style={{ fontWeight: 600 }}>All {(events.find(e => String(e.id) === String(selectedEvent)) || {}).series_size} days of this event</span>
              </label>
            )}
            {/* Session S: explicit SMS opt-in, default OFF, count visible */}
            {sendToEvent && selectedEvent && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={smsNudge} onChange={e => setSmsNudge(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                <span style={{ fontWeight: 600 }}>Also send SMS{smsCount != null ? ` — ${smsCount} recipient${smsCount === 1 ? '' : 's'}` : ''}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({smsCount != null ? `~${smsCount} credit${smsCount === 1 ? '' : 's'}` : 'counting…'})</span>
              </label>
            )}
          </div>

          <button
            onClick={submit}
            disabled={saving}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {saving ? 'Sending...' : sendToEvent && selectedEvent ? 'Send to Event Staff' : 'Send to All Staff'}
          </button>
        </div>

        {/* Sent history */}
        {sentUpdates.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => setShowSent(v => !v)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}
            >
              <span>Sent History ({sentUpdates.length})</span>
              <span style={{ fontSize: 11, transform: showSent ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {showSent && (
              <div style={{ marginTop: 8 }}>
                {sentUpdates.map(u => (
                  <div key={u.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{u.title}</div>
                    <MUpdateMessage text={u.message} style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main mobile agent dashboard ───────────────────────────────────────────────
export default function AgentMobileDashboard() {
  const { logout } = useAuth();
  const [active, setActive] = useState('applications');


  return (
    <div className="app-shell">
      {/* Admin header bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20,
        background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
        padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        maxWidth: 430, margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px' }}>
            <span style={{ color: 'var(--text-primary)' }}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
            <span style={{ color: 'var(--accent)' }}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
          </span>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.8px',
            background: 'var(--accent)', color: '#fff',
            padding: '2px 7px', borderRadius: 20,
          }}>
            ADMIN
          </span>
        </div>
        <button
          onClick={logout}
          style={{ fontSize: 12, color: 'var(--text-muted)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign out
        </button>
      </div>

      {/* Page content — offset for fixed header */}
      <div style={{ paddingTop: 52 }}>
        {active === 'applications' && <MobileApplications />}
        {active === 'events' && <MobileEvents />}
        {active === 'staff' && <MobileStaff />}
        {active === 'comms' && <MobileComms />}
      </div>

      <AgentBottomNav active={active} setActive={setActive} />
    </div>
  );
}
